import type { LanguageCopy, LanguageKey } from "../api/client";

type BarChartProps = {
  languageOrder: LanguageKey[];
  languages: Record<LanguageKey, LanguageCopy>;
  youScores: Record<LanguageKey, number>;
  partnerScores?: Record<LanguageKey, number>;
  partnerName?: string;
  /** When true and `partnerScores` is absent, render an empty placeholder
   * bar per language — a "seat saved" for the partner so the waiting-state
   * chart has the same shape as the eventual both-done chart. */
  partnerPending?: boolean;
};

const MAX_SCORE = 6;

function Bar({
  score,
  label,
  fillColor,
}: {
  score: number;
  label?: string;
  fillColor: string;
}) {
  const pct = (score / MAX_SCORE) * 100;
  return (
    <div className="flex items-center gap-4 text-[0.75rem]">
      {label && (
        <span className="wren-eyebrow w-14 shrink-0 text-[0.625rem]">{label}</span>
      )}
      <div
        className="relative h-2 flex-1 overflow-hidden bg-[color:var(--color-wren-border-subtle)]"
        style={{ borderRadius: 4 }}
      >
        <div
          className="absolute inset-y-0 left-0"
          style={{ width: `${pct}%`, backgroundColor: fillColor, borderRadius: 4 }}
        />
      </div>
      <span className="w-10 shrink-0 text-right font-semibold tabular-nums text-[color:var(--color-wren-gray-700)]">
        {score}/{MAX_SCORE}
      </span>
    </div>
  );
}

function GhostBar({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-4 text-[0.75rem] opacity-60">
      {label && (
        <span className="wren-eyebrow w-14 shrink-0 text-[0.625rem]">{label}</span>
      )}
      <div
        className="relative h-2 flex-1 overflow-hidden border border-dashed"
        style={{
          borderRadius: 4,
          borderColor: "var(--color-wren-light-brown)",
          backgroundColor: "transparent",
        }}
        aria-hidden="true"
      />
      {/* Screen-reader context: each ghost row reads as "<partner>: pending". */}
      <span className="sr-only">pending</span>
      <span
        aria-hidden="true"
        className="w-10 shrink-0 text-right tabular-nums text-[color:var(--color-wren-light-brown)]"
      >
        —
      </span>
    </div>
  );
}

export function BarChart({
  languageOrder,
  languages,
  youScores,
  partnerScores,
  partnerName,
  partnerPending,
}: BarChartProps) {
  const youColor = "var(--color-wren-green)";
  const partnerColor = "var(--color-wren-light-brown)";
  // Labels are redundant when only "You" shows up. They're meaningful both when
  // partner data is present AND when a ghost partner row is being reserved.
  const showLabels = Boolean(partnerScores) || Boolean(partnerPending);
  const partnerLabel = partnerName ?? "Them";

  return (
    <div className="space-y-6">
      {languageOrder.map((key, i) => (
        <div
          key={key}
          className="wren-bar-row"
          style={{ animationDelay: `${i * 70}ms` }}
        >
          <div className="mb-2 text-[0.875rem] font-semibold text-[color:var(--color-wren-text)]">
            {languages[key].display_name}
          </div>
          <div className="space-y-2">
            <Bar
              score={youScores[key] ?? 0}
              label={showLabels ? "You" : undefined}
              fillColor={youColor}
            />
            {partnerScores ? (
              <Bar
                score={partnerScores[key] ?? 0}
                label={showLabels ? partnerLabel : undefined}
                fillColor={partnerColor}
              />
            ) : partnerPending ? (
              <GhostBar label={showLabels ? partnerLabel : undefined} />
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
